import React, { useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import EmbeddedYouVisit from '../../../components/EmbeddedYouVisit';
import withHostContext, {
  trackYouVisit,
  trackingLocations,
} from '../../../context/host/consumer';
import { CLICK_TRACKING_APPLICATION_NAVIANCE_COLLEGE_PROFILE_PAGE } from '../../../utils/clickTrackingManager';
import './styles.css';

// like an array 'find' function, but for a NodeList object
/* istanbul ignore next */
const findFirst = (nodeList, findFunc) => {
  for (let i = 0; i < nodeList.length; i++) {
    if (findFunc(nodeList[i])) {
      return nodeList[i];
    }
  }
  return null;
};

// https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
const useMutationObserver = (
  ref,
  callback,
  options = {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  },
) => {
  useEffect(() => {
    /* istanbul ignore else */
    if (ref.current) {
      const observer = new MutationObserver(callback);
      observer.observe(ref.current, options);
      return () => observer.disconnect();
    }
  }, [ref, callback, options]);
};

function TrackingYouVisitBanner({
  ipedscode,
  scid,
  trackView,
  trackClick,
  hostContext,
  initialMouseOverState = false,
  initialYouvisitDivInViewState = false,
  initialframeLoadedState = false,
}) {
  const mouseOverState = useRef(initialMouseOverState); // help determine clicks
  const youvisitDivInViewState = useRef(initialYouvisitDivInViewState); // determine if youvisit is in view
  const iframeLoadedState = useRef(initialframeLoadedState); // determine if youvisit iframe loaded

  const mouseOverIframeIn = () => (mouseOverState.current = true);
  const mouseOverIframeOut = () => (mouseOverState.current = false);

  const clickHandler = () => {
    /* istanbul ignore else */
    if (mouseOverState.current) {
      trackClick(hostContext, {
        type: trackYouVisit.CLICK,
        productId: 0,
        location: '',
        scid: scid,
        application: CLICK_TRACKING_APPLICATION_NAVIANCE_COLLEGE_PROFILE_PAGE,
      });
    }
  };

  // detectRenderRef - use this to determine if our div has loaded
  // so we can add click and view tracking event listeners
  const cleanupRef = useRef(null);
  const detectRenderRef = useCallback((node) => {
    if (cleanupRef.current) cleanupRef.current();
    // if there's a node, then add the event listeners
    if (node) {
      node.addEventListener('mouseover', mouseOverIframeIn);
      node.addEventListener('mouseout', mouseOverIframeOut);
      window.addEventListener('blur', clickHandler);

      // setup the cleanup ref so it can be called upon unmount
      cleanupRef.current = () => {
        node.removeEventListener('mouseover', mouseOverIframeIn);
        node.removeEventListener('mouseout', mouseOverIframeOut);
        window.removeEventListener('blur', clickHandler);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrackView = () => {
    /* istanbul ignore else */
    if (ipedscode && youvisitDivInViewState.current && iframeLoadedState.current) {
      trackView(hostContext, {
        type: trackYouVisit.VIEW,
        location: trackingLocations.HUBPAGE,
        scid: scid,
        application: CLICK_TRACKING_APPLICATION_NAVIANCE_COLLEGE_PROFILE_PAGE,
      });
    }
  };

  // for the MutationRecord type, see https://developer.mozilla.org/en-US/docs/Web/API/MutationRecord
  /* istanbul ignore next */
  const mutationHandler = (mutationRecords, callingObserver) => {
    // we're looking for an iframe to be added if there's a valid youvisit tour
    const addedIframeRecord = mutationRecords.find((rec) => {
      return (
        rec.type === 'childList' &&
        rec.addedNodes?.length &&
        findFirst(rec.addedNodes, (node) => node.nodeName === 'IFRAME')
      );
    });
    if (addedIframeRecord) {
      iframeLoadedState.current = true;
      handleTrackView();
      callingObserver.disconnect();
    }
  };

  const toggleYouVisitInView = (inView) => {
    youvisitDivInViewState.current = inView;
    handleTrackView();
  };

  // IntersectionObserver Hook - use to determine if the youvisit was screen viewable
  const [viewedOnScreenRef] = useInView({
    threshold: 0.75,
    triggerOnce: true,
    onChange: toggleYouVisitInView,
  });
  const mutationRef = useRef(null);
  useMutationObserver(mutationRef, mutationHandler);

  // the 'youvisit-detector-id' div determines when this HTML is on screen so we can hook up the event listeners
  // the 'youvisit-container-id' div determines when it is displayed in the viewport
  // the 'youvisit-mutation-container-id' determines when an iframe is added
  return (
    <div id="youvisit-detector-id" ref={detectRenderRef} data-cy="youvisit-detector-id">
      <div
        id="youvisit-container-id"
        className="youvisit-banner-container"
        data-cy="youvisit-container-id"
        ref={viewedOnScreenRef}
      >
        <div
          id="youvisit-mutation-container-id"
          data-cy="youvisit-mutation-container"
          ref={mutationRef}
        >
          <EmbeddedYouVisit ipedscode={ipedscode} />
        </div>
      </div>
    </div>
  );
}

TrackingYouVisitBanner.propTypes = {
  ipedscode: PropTypes.string,
  scid: PropTypes.number,
  hostContext: PropTypes.object,
  trackClick: PropTypes.func,
  trackView: PropTypes.func,
};

export default withHostContext(TrackingYouVisitBanner);
